import { Phone, Mail, MapPin, Camera, Share2, Music, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { business } from "../data/business";
import { useBusinessPhone } from "../hooks/useBusinessPhone";
import { AddressLine } from "./AddressLine";
import { Newsletter } from "./Newsletter";

export function Footer() {
  const year = new Date().getFullYear();
  const { phone, phoneRaw } = useBusinessPhone();

  return (
    <footer className="footer">
      <div className="divider" />
      <div className="ft-row">
        <div className="ft-brand ft-col">
          <div className="ft-lockup">
            <span className="ft-seal" aria-hidden="true">
              <img src="/ocoee-seal.png" alt="" loading="lazy" decoding="async" />
            </span>
            <h3>OCOEE RIVER<br />EXPERIENCE</h3>
          </div>
          <p>
            Whitewater rafting, on-site lodging, and dining near the Ocoee
            River — home of the 1996 Olympic whitewater events.
          </p>
          <a href={`tel:${phoneRaw}`} style={{ color: "var(--accent-gold)", fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: 15, marginTop: 8 }}>
            <Phone aria-hidden="true" /> {phone}
          </a>
          <Newsletter />
        </div>

        <div className="ft-col">
          <h4>EXPLORE</h4>
          <Link to="/">Home</Link>
          <Link to="/rafting">Rafting</Link>
          <Link to="/lodge">Ocoee Whitewater Lodge</Link>
          <Link to="/bunkhouse">Bunkhouse</Link>
          <Link to="/camping">Camping & RV</Link>
          <Link to="/hikes">Hikes & Waterfalls</Link>
          <Link to="/gallery">Gallery</Link>
          <Link to="/events">Events</Link>
          <Link to="/shop">Shop</Link>
        </div>

        <div className="ft-col">
          <h4>DINING</h4>
          <Link to="/sugarloaf">Sugarloaf Bar & Grill</Link>
          <Link to="/bbq">Blue Hole BBQ</Link>
          <h4 style={{ marginTop: 10 }}>CONTACT</h4>
          <Link to="/contact"><Mail aria-hidden="true" /> Email & Hours</Link>
          <a
            href={`https://maps.google.com/?q=${encodeURIComponent(business.address.full)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MapPin aria-hidden="true" /> <AddressLine />
          </a>
        </div>

        {/* item 69: top 5 helpful links surfaced; secondary links collapsed under details */}
        <div className="ft-col">
          <h4>CONNECT</h4>
          {/* #82: descriptive aria-labels naming the business + platform so
              screen-reader users get full context, not just the platform name.
              Anchors carry an explicit .ft-social class so the hover state can
              promote the link to the brand gold rather than the muted soft-gold
              used by other footer links. */}
          <a
            href={business.social.instagram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Ocoee River Experience on Instagram (opens in a new tab)"
            className="ft-social"
          >
            <Camera aria-hidden="true" /> Instagram
          </a>
          <a
            href={business.social.facebook}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Ocoee River Experience on Facebook (opens in a new tab)"
            className="ft-social"
          >
            <Share2 aria-hidden="true" /> Facebook
          </a>
          <a
            href={business.social.tiktok}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Ocoee River Experience on TikTok (opens in a new tab)"
            className="ft-social"
          >
            <Music aria-hidden="true" /> TikTok
          </a>

          <h4 style={{ marginTop: 10 }}>HELPFUL</h4>
          <Link to="/faq">FAQ</Link>
          <Link to="/what-to-bring">What to Bring</Link>
          <Link to="/safety">Safety on the Water</Link>
          <Link to="/cancellation">Cancellation Policy</Link>
          <Link to="/waiver">Waiver & Liability</Link>

          <details className="ft-more">
            <summary>More links</summary>
            <a
              href={business.social.twitter}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Ocoee River Experience on Twitter / X (opens in a new tab)"
              className="ft-social"
            >
              <span style={{ width: 16, height: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--accent-gold)", fontSize: 14, fontWeight: 700 }}>𝕏</span>
              Twitter / X
            </a>
            {/* Groups / Weddings / Schools collapsed under /events — the
                original site has a single "Events" page; we don't fan it out
                into separate offerings until each program is confirmed. */}
            <Link to="/events">Events &amp; Group Trips</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/accessibility">Accessibility</Link>
          </details>
        </div>
      </div>
      {/* Trust strip: payment methods accepted at the TheFlyBook checkout
          (their standard processor lineup — verified by their public
          docs). "Licensed by TN Dept. of Tourist Development" and "U.S.
          Forest Service permitted · Cherokee NF" were removed in this
          revision — both were fabricated certifications added by an
          earlier polish pass that didn't follow the no-invent policy.
          Re-add ONLY when the operator confirms which licenses they
          actually hold and provides the exact wording.
          NEEDS-VERIFY: operator confirmation on licensing / permit text. */}
      <div className="ft-trust-strip" aria-label="Trust and payment">
        <span className="ft-trust-item">
          <Lock size={14} aria-hidden="true" /> Secure booking via TheFlyBook
        </span>
        <span className="ft-trust-item ft-trust-pay">
          <span className="ft-pay">VISA</span>
          <span className="ft-pay">MC</span>
          <span className="ft-pay">AMEX</span>
          <span className="ft-pay">Discover</span>
        </span>
      </div>

      <div className="divider" />
      <div className="ft-bot">
        <p>© {year} {business.name} — All Rights Reserved</p>
        <span className="ft-loc"><MapPin aria-hidden="true" /> {business.address.city}, {business.address.state} · All times ET · TVA Release Calendar</span>
      </div>
    </footer>
  );
}
